import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1';

// ── In-memory cache for static-ish data ─────────────────────────────────────
const _cache = {};
const _inflight = {};

const cached = (key, fetcher, ttlMs = 5 * 60 * 1000) => {
  const now = Date.now();
  if (_cache[key] && now - _cache[key].ts < ttlMs) return Promise.resolve(_cache[key].data);
  if (_inflight[key]) return _inflight[key];
  _inflight[key] = fetcher().then((data) => {
    _cache[key] = { data, ts: Date.now() };
    delete _inflight[key];
    return data;
  }).catch((err) => { delete _inflight[key]; throw err; });
  return _inflight[key];
};

export const invalidateCache = (key) => { delete _cache[key]; };
/**
 * Turns a photo URL from the API into something an <img src> can load.
 *
 * The backend hands back one of two things depending on where the salon stores
 * photos: an absolute presigned S3 URL, or a relative /api/v1/.../serve/<token>
 * path for local storage. Resolving against the API base handles both — an
 * absolute URL passes straight through.
 */
export const mediaUrl = (path) => {
  if (!path) return null;
  try { return new URL(path, API_BASE_URL).href; } catch { return path; }
};

// ────────────────────────────────────────────────────────────────────────────

// Create axios instance
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - Add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - Handle errors with automatic retry for server errors
apiClient.interceptors.response.use(
  (response) => {
    return response.data;
  },
  async (error) => {
    const originalRequest = error.config;

    // Auto-retry on server errors or timeouts (serverless cold start handling)
    const isRetryable =
      (!error.response || error.response.status >= 500 || error.code === 'ECONNABORTED') &&
      !originalRequest._retryCount &&
      // Some requests are expensive enough that retrying one is worse than
      // failing it — see services.sopGuidePdf.
      !originalRequest._noRetry;

    if (isRetryable) {
      originalRequest._retryCount = (originalRequest._retryCount || 0) + 1;
      if (originalRequest._retryCount <= 2) {
        // Wait briefly before retrying (cold start recovery)
        await new Promise((r) => setTimeout(r, 1000 * originalRequest._retryCount));
        return apiClient(originalRequest);
      }
    }

    // Handle 401 Unauthorized — clear token and let AuthContext handle redirect
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      // Skip logout redirect for auth endpoints (login, register)
      const isAuthRoute = originalRequest.url?.includes('/auth/login') ||
                          originalRequest.url?.includes('/auth/register');
      if (!isAuthRoute) {
        localStorage.removeItem('token');
        // Dispatch a custom event so AuthContext can react
        window.dispatchEvent(new Event('auth:logout'));
      }
    }

    return Promise.reject(error.response?.data || error);
  }
);

export default apiClient;

// Helper functions for common requests
export const api = {
  // GET request
  get: (url, config = {}) => apiClient.get(url, config),

  // POST request
  post: (url, data, config = {}) => apiClient.post(url, data, config),

  // PUT request
  put: (url, data, config = {}) => apiClient.put(url, data, config),

  // PATCH request
  patch: (url, data, config = {}) => apiClient.patch(url, data, config),

  // DELETE request
  delete: (url, config = {}) => apiClient.delete(url, config),

  // Auth
  auth: {
    login: (data) => apiClient.post('/auth/login', data),
    register: (data) => apiClient.post('/auth/register', data),
    logout: () => apiClient.post('/auth/logout'),
    getMe: () => apiClient.get('/auth/me'),
    updateProfile: (data) => apiClient.put('/auth/update-profile', data),
    changePassword: (data) => apiClient.put('/auth/change-password', data),
    forgotPassword: (data) => apiClient.post('/auth/forgot-password', data),
  },

  // Appointments
  appointments: {
    list: (params = {}) => apiClient.get('/appointments', { params }),
    get: (id) => apiClient.get(`/appointments/${id}`),
    create: (data) => apiClient.post('/appointments', data),
    update: (id, data) => apiClient.put(`/appointments/${id}`, data),
    delete: (id) => apiClient.delete(`/appointments/${id}`),
    checkin: (id) => apiClient.put(`/appointments/${id}/checkin`),
    complete: (id, data) => apiClient.put(`/appointments/${id}/complete`, data),
    cancel: (id, data) => apiClient.put(`/appointments/${id}/cancel`, data),
    reschedule: (id, data) => apiClient.put(`/appointments/${id}/reschedule`, data),
    availableSlots: (params) => apiClient.get('/appointments/slots/available', { params }),
  },

  // Invoices (Billing)
  invoices: {
    list: (params = {}) => apiClient.get('/invoices', { params }),
    get: (id) => apiClient.get(`/invoices/${id}`),
    create: (data) => apiClient.post('/invoices', data),
    update: (id, data) => apiClient.patch(`/invoices/${id}`, data),
    issue: (id) => apiClient.put(`/invoices/${id}/issue`),
    addPayment: (id, data) => apiClient.post(`/invoices/${id}/payment`, data),
    getPDF: (id) => apiClient.get(`/invoices/${id}/pdf`),
    void: (id) => apiClient.put(`/invoices/${id}/void`),
    refund: (id, data) => apiClient.post(`/invoices/${id}/refund`, data),
    dailySummary: (params = {}) => apiClient.get('/invoices/summary/daily', { params }),
  },

  // Customers
  // Salon AI mobile app consultations — the bridal / men's / women's / analysis
  // sessions the app records. Photo URLs come back signed and time-limited.
  beautyProfiles: {
    list: (params = {}) => apiClient.get('/beauty-profiles', { params }),
    get: (customerId) => apiClient.get(`/beauty-profiles/${customerId}`),
  },

  // Physical membership cards: minted in print runs, handed over later, then
  // scanned. The path stays /vip-cards — renaming it would break nothing but
  // buy nothing either.
  // at the counter.
  vipCards: {
    list: (params = {}) => apiClient.get('/vip-cards', { params }),
    summary: () => apiClient.get('/vip-cards/summary'),
    lookup: (code) => apiClient.get(`/vip-cards/lookup/${encodeURIComponent(code)}`),
    createBatch: (data) => apiClient.post('/vip-cards/batch', data),
    assign: (id, customerId) => apiClient.patch(`/vip-cards/${id}/assign`, { customerId }),
    updateStatus: (id, data) => apiClient.patch(`/vip-cards/${id}/status`, data),
    send: (id, imageBase64) => apiClient.post(`/vip-cards/${id}/send`, { imageBase64 }),
  },

  serviceReminders: {
    list: (params = {}) => apiClient.get('/service-reminders', { params }),
    forCustomer: (customerId, params = {}) =>
      apiClient.get('/service-reminders', { params: { ...params, customerId, status: 'pending' } }),
    summary: () => apiClient.get('/service-reminders/summary'),
    cancel: (id) => apiClient.patch(`/service-reminders/${id}/cancel`),
    getConfig: () => apiClient.get('/service-reminders/config'),
    updateConfig: (data) => apiClient.put('/service-reminders/config', data),
    services: () => apiClient.get('/service-reminders/services'),
    updateService: (id, data) => apiClient.put(`/service-reminders/services/${id}`, data),
    run: (dryRun = false) => apiClient.post(`/service-reminders/run?dryRun=${dryRun}`),
  },

  aiConsultations: {
    list: (params = {}) => apiClient.get('/ai-consultations', { params }),
    get: (id) => apiClient.get(`/ai-consultations/${id}`),
    forCustomer: (customerId, params = {}) =>
      apiClient.get(`/customers/${customerId}/ai-consultations`, { params }),
  },

  customers: {
    entitlements: (id) => apiClient.get(`/customers/${id}/entitlements`),
    risk: () => apiClient.get('/customers/risk'),
    list: (params = {}) => apiClient.get('/customers', { params }),
    get: (id) => apiClient.get(`/customers/${id}`),
    create: (data) => apiClient.post('/customers', data),
    update: (id, data) => apiClient.put(`/customers/${id}`, data),
    delete: (id) => apiClient.delete(`/customers/${id}`),
    search: (query) => apiClient.get('/customers/search', { params: { q: query } }),
    history: (id, params) => apiClient.get(`/customers/${id}/history`, { params }),
    breakdown: () => apiClient.get('/customers/breakdown'),
    glance: (id) => apiClient.get(`/customers/${id}/glance`),
    membershipOffer: (id) => apiClient.get(`/customers/${id}/membership-offer`),
    segments: () => apiClient.get('/customers/segments'),
    stats: () => apiClient.get('/customers/stats'),
    wallet: (id) => apiClient.get(`/customers/${id}/wallet`),
    adjustWallet: (id, data) => apiClient.post(`/customers/${id}/wallet/adjust`, data),
    sendMembershipCard: (id, imageBase64) => apiClient.post(`/customers/${id}/membership-card/send`, { imageBase64 }),
  },

  // Beauty Assessments

  concerns: {
    map: () => apiClient.get('/concerns'),
    get: (id) => apiClient.get(`/concerns/${id}`),
  },

  // Employees
  employees: {
    list: (params = {}) => {
      const key = `employees:${JSON.stringify(params)}`;
      return cached(key, () => apiClient.get('/employees', { params }));
    },
    get: (id) => apiClient.get(`/employees/${id}`),
    create: (data) => { Object.keys(_cache).filter(k => k.startsWith('employees:')).forEach(k => delete _cache[k]); return apiClient.post('/employees', data); },
    update: (id, data) => { Object.keys(_cache).filter(k => k.startsWith('employees:')).forEach(k => delete _cache[k]); return apiClient.put(`/employees/${id}`, data); },
    delete: (id) => { Object.keys(_cache).filter(k => k.startsWith('employees:')).forEach(k => delete _cache[k]); return apiClient.delete(`/employees/${id}`); },
    getMe: () => apiClient.get('/employees/me'),
    getSchedule: (params) => apiClient.get('/employees/schedule/get', { params }),
    updateSchedule: (id, data) => apiClient.put(`/employees/${id}/schedule`, data),
    getPerformance: (params) => apiClient.get('/employees/performance/get', { params }),
    getDailyAttendance: (params) => apiClient.get('/employees/attendance/daily', { params }),
    markAttendance: (data) => apiClient.post('/employees/attendance/mark', data),
    getAttendance: (id, params) => apiClient.get(`/employees/${id}/attendance`, { params }),
    getStats: (id, params) => apiClient.get(`/employees/${id}/stats`, { params }),
    getTargets: (id, params) => apiClient.get(`/employees/${id}/targets`, { params }),
    upsertTarget: (id, data) => apiClient.post(`/employees/${id}/targets`, data),
    getProductUsage: (id, params) => apiClient.get(`/employees/${id}/product-usage`, { params }),
    addProductUsage: (id, data) => apiClient.post(`/employees/${id}/product-usage`, data),
    deleteProductUsage: (id, usageId) => apiClient.delete(`/employees/${id}/product-usage/${usageId}`),
  },

  // Services
  services: {
    list: (params = {}) => {
      const key = `services:${JSON.stringify(params)}`;
      return cached(key, () => apiClient.get('/services', { params }));
    },
    get: (id) => apiClient.get(`/services/${id}`),
    // Most-billed services, for the billing quick-add row.
    top: (params = {}) => apiClient.get('/services/top', { params }),
    create: (data) => { invalidateCache('services:{}'); return apiClient.post('/services', data); },
    update: (id, data) => { Object.keys(_cache).filter(k => k.startsWith('services:')).forEach(k => delete _cache[k]); return apiClient.put(`/services/${id}`, data); },
    delete: (id) => { Object.keys(_cache).filter(k => k.startsWith('services:')).forEach(k => delete _cache[k]); return apiClient.delete(`/services/${id}`); },
    // What each service consumes and what it earns. Uncached: it is edited on
    // the page that reads it.
    costing: (params = {}) => apiClient.get('/services/costing', { params }),
    profitability: (params = {}) => apiClient.get('/services/profitability', { params }),
    updateRecipe: (id, lines) => {
      Object.keys(_cache).filter(k => k.startsWith('services:')).forEach(k => delete _cache[k]);
      return apiClient.put(`/services/${id}/recipe`, { lines });
    },
    // A4 to pin up, or 80mm for the counter printer. Returns the PDF itself.
    sopPdf: (id, params = {}) => apiClient.get(`/services/${id}/sop.pdf`, { params, responseType: 'blob' }),
    /*
     * The whole procedure book, one language per file; `category` narrows it
     * to a section, because the complete book runs past eight hundred pages.
     *
     * Three minutes rather than the usual thirty seconds, and no retry. All
     * 215 services take about 77 seconds to typeset on the server, measured —
     * so the default timeout would abort it and the automatic retry would then
     * start a second 77-second build on a box with 909MB of memory. One slow
     * request is fine; two concurrent ones are not.
     */
    sopGuidePdf: (params = {}) => apiClient.get('/services/sop-guide.pdf', {
      params, responseType: 'blob', timeout: 180000, _noRetry: true,
    }),
    // The printed price list, built from the live services so the card on the
    // counter and the till cannot disagree.
    menuPdf: (gender) => apiClient.get('/services/menu.pdf', { params: { gender }, responseType: 'blob' }),
    hygienePdf: () => apiClient.get('/services/hygiene.pdf', { responseType: 'blob' }),
    // 'hair' or 'skin': the A5 reference guides kept in the salon.
    guidePdf: (book) => apiClient.get(`/services/guides/${book}.pdf`, { responseType: 'blob' }),
    categories: {
      list: () => apiClient.get('/services/categories'),
      create: (data) => apiClient.post('/services/categories', data),
      update: (id, data) => apiClient.put(`/services/categories/${id}`, data),
      delete: (id) => apiClient.delete(`/services/categories/${id}`),
    },
    byCategory: (categoryId) => apiClient.get(`/services/category/${categoryId}`),
    productRecs: (id, params) => apiClient.get(`/services/${id}/product-recs`, { params }),
  },

  // Products
  products: {
    list: (params = {}) => {
      const key = `products:${JSON.stringify(params)}`;
      return cached(key, () => apiClient.get('/products', { params }));
    },
    get: (id) => apiClient.get(`/products/${id}`),
    create: (data) => { Object.keys(_cache).filter(k => k.startsWith('products:')).forEach(k => delete _cache[k]); return apiClient.post('/products', data); },
    update: (id, data) => { Object.keys(_cache).filter(k => k.startsWith('products:')).forEach(k => delete _cache[k]); return apiClient.put(`/products/${id}`, data); },
    delete: (id) => { Object.keys(_cache).filter(k => k.startsWith('products:')).forEach(k => delete _cache[k]); return apiClient.delete(`/products/${id}`); },
    updateStock: (id, data) => apiClient.put(`/products/${id}/stock`, data),
    inventoryReport: () => apiClient.get('/products/inventory/report'),
    lowStock: () => apiClient.get('/products/inventory/low-stock'),
    setKind: (id, kind) => apiClient.patch(`/products/${id}/kind`, { kind }),
    reviewGroups: () => apiClient.get('/products/review-groups'),
    taxonomy: () => apiClient.get('/products/taxonomy'),
    suggest: (barcode, name) => apiClient.get('/products/suggest', { params: { barcode, name } }),
    recommend: (params) => apiClient.get('/products/recommend', { params }),
    setKindBulk: (ids, kind) => apiClient.patch('/products/kind/bulk', { ids, kind }),
  },

  // Somebody at the desk noticing the shelf is nearly empty.
  stockRequests: {
    list: (params = {}) => apiClient.get('/stock-requests', { params }),
    create: (data) => apiClient.post('/stock-requests', data),
    setStatus: (id, data) => apiClient.put(`/stock-requests/${id}/status`, data),
    remove: (id) => apiClient.delete(`/stock-requests/${id}`),
  },

  walletGrants: {
    list: (params = {}) => apiClient.get('/wallet-grants', { params }),
    summary: (params = {}) => apiClient.get('/wallet-grants/summary', { params }),
    create: (data) => apiClient.post('/wallet-grants', data),
    revoke: (id, data) => apiClient.put(`/wallet-grants/${id}/revoke`, data),
  },

  staffTime: {
    month: (params = {}) => apiClient.get('/staff-time', { params }),
    save: (data) => apiClient.post('/staff-time', data),
    setHours: (data) => apiClient.post('/staff-time/hours', data),
    summary: (params = {}) => apiClient.get('/staff-time/summary', { params }),
  },

  staffNotes: {
    list: (params = {}) => apiClient.get('/staff-notes', { params }),
    summary: (params = {}) => apiClient.get('/staff-notes/summary', { params }),
    create: (data) => apiClient.post('/staff-notes', data),
    update: (id, data) => apiClient.put(`/staff-notes/${id}`, data),
    setStatus: (id, data) => apiClient.put(`/staff-notes/${id}/status`, data),
    remove: (id) => apiClient.delete(`/staff-notes/${id}`),
  },

  salonCalendar: {
    list: (params = {}) => apiClient.get('/salon-calendar', { params }),
    create: (data) => apiClient.post('/salon-calendar', data),
    update: (id, data) => apiClient.put(`/salon-calendar/${id}`, data),
    delete: (id) => apiClient.delete(`/salon-calendar/${id}`),
  },

  // Packages & Memberships
  packages: {
    list: (params = {}) => {
      const key = `packages:${JSON.stringify(params)}`;
      return cached(key, () => apiClient.get('/packages', { params }));
    },
    get: (id) => apiClient.get(`/packages/${id}`),
    membershipSummary: () => apiClient.get('/packages/membership-summary'),
    create: (data) => { Object.keys(_cache).filter(k => k.startsWith('packages:')).forEach(k => delete _cache[k]); return apiClient.post('/packages', data); },
    update: (id, data) => { Object.keys(_cache).filter(k => k.startsWith('packages:')).forEach(k => delete _cache[k]); return apiClient.put(`/packages/${id}`, data); },
    delete: (id) => { Object.keys(_cache).filter(k => k.startsWith('packages:')).forEach(k => delete _cache[k]); return apiClient.delete(`/packages/${id}`); },
    memberships: {
      list: () => apiClient.get('/packages/memberships'),
      get: (id) => apiClient.get(`/packages/memberships/${id}`),
      create: (data) => apiClient.post('/packages/memberships', data),
      update: (id, data) => apiClient.put(`/packages/memberships/${id}`, data),
      delete: (id) => apiClient.delete(`/packages/memberships/${id}`),
    },
  },

  // Payroll
  payroll: {
    get: (params = {}) => apiClient.get('/payroll', { params }),
  },

  // Reports
  targets: {
    scorecard: (params = {}) => apiClient.get('/targets/scorecard', { params }),
    list:      (params = {}) => apiClient.get('/targets', { params }),
    save:      (payload)     => apiClient.put('/targets', payload),
  },

  reports: {
    sales: (params = {}) => apiClient.get('/reports/sales', { params }),
    revenue: (params = {}) => apiClient.get('/reports/revenue', { params }),
    services: (params = {}) => apiClient.get('/reports/services', { params }),
    customers: (params = {}) => apiClient.get('/reports/customers', { params }),
    employees: (params = {}) => apiClient.get('/reports/employees', { params }),
    expenses: (params = {}) => apiClient.get('/reports/expenses', { params }),
    hourly: (params = {}) => apiClient.get('/reports/sales', { params: { groupBy: 'hour', ...params } }),
    productUsageCost: (params = {}) => apiClient.get('/reports/product-usage-cost', { params }),
    productCogs: (params = {}) => apiClient.get('/reports/product-cogs', { params }),
    staffHoursDaily: (params = {}) => apiClient.get('/reports/staff-hours-daily', { params }),
    customerInsights: (params = {}) => apiClient.get('/reports/customer-insights', { params }),
    customerRetention: (params = {}) => apiClient.get('/reports/customer-retention', { params }),
    serviceInsights: (params = {}) => apiClient.get('/reports/service-insights', { params }),
    serviceCombos: (params = {}) => apiClient.get('/reports/service-combos', { params }),
    // What is growing and what is fading, by week or by month.
    serviceTrends: (params = {}) => apiClient.get('/reports/service-trends', { params }),
    staffInsights: (params = {}) => apiClient.get('/reports/staff-insights', { params }),
    // The recognition board: five readings of one period, one call.
    recognition: (params = {}) => apiClient.get('/reports/recognition', { params }),
    // Employee of the month: a choice the owner makes, not a calculation.
    eotm:        (params = {}) => apiClient.get('/reports/recognition/eotm', { params }),
    eotmHistory: () => apiClient.get('/reports/recognition/eotm/history'),
    setEotm:     (body) => apiClient.put('/reports/recognition/eotm', body),
    clearEotm:   (params = {}) => apiClient.delete('/reports/recognition/eotm', { params }),
    // A blob, not JSON: the certificate is a real file, signed for and kept.
    eotmCertificate: (params = {}) =>
      apiClient.get('/reports/recognition/eotm/certificate', { params, responseType: 'blob' }),
    productInsights: (params = {}) => apiClient.get('/reports/product-insights', { params }),
  },

  // Salon (Dashboard + Settings)
  salon: {
    get: (id) => cached(`salon:${id}`, () => apiClient.get(`/salons/${id}`), 10 * 60 * 1000),
    update: (id, data) => { Object.keys(_cache).filter(k => k.startsWith('salon:')).forEach(k => delete _cache[k]); return apiClient.put(`/salons/${id}`, data); },
    updateSettings: (id, data) => { Object.keys(_cache).filter(k => k.startsWith('salon:')).forEach(k => delete _cache[k]); return apiClient.put(`/salons/${id}/settings`, data); },
    testStorage: () => apiClient.post('/salons/storage/test'),
    // Without dates the endpoint defaults to the current month, and builds that
    // range in server-local time rather than IST. Passing explicit dates takes
    // the istRange path instead, which is the correct one.
    dashboard: (id, params) => apiClient.get(`/salons/${id}/dashboard`, { params }),
    // Standing totals, deliberately without a date range — see the overview page.
    overview:  (id)         => apiClient.get(`/salons/${id}/overview`),
  },

  // Users (Staff Management)
  users: {
    list: () => apiClient.get('/users'),
    get: (id) => apiClient.get(`/users/${id}`),
    create: (data) => apiClient.post('/users', data),
    update: (id, data) => apiClient.put(`/users/${id}`, data),
    delete: (id) => apiClient.delete(`/users/${id}`),
  },

  // Offers
  offers: {
    list: () => cached('offers', () => apiClient.get('/offers')),
    get: (id) => apiClient.get(`/offers/${id}`),
    create: (data) => { delete _cache['offers']; return apiClient.post('/offers', data); },
    update: (id, data) => { delete _cache['offers']; return apiClient.put(`/offers/${id}`, data); },
    delete: (id) => { delete _cache['offers']; return apiClient.delete(`/offers/${id}`); },
  },

  // Product Usage (stylist end-of-day log)
  productUsage: {
    list: (params) => apiClient.get('/product-usage', { params }),
    add: (data) => apiClient.post('/product-usage', data),
    delete: (id) => apiClient.delete(`/product-usage/${id}`),
    listAssignments: (params) => apiClient.get('/product-usage/assignments', { params }),
    createAssignment: (data) => apiClient.post('/product-usage/assignments', data),
    deleteAssignment: (id) => apiClient.delete(`/product-usage/assignments/${id}`),
  },

  // Campaigns (Marketing)
  campaigns: {
    list: (params = {}) => apiClient.get('/campaigns', { params }),
    get: (id) => apiClient.get(`/campaigns/${id}`),
    create: (data) => apiClient.post('/campaigns', data),
    update: (id, data) => apiClient.put(`/campaigns/${id}`, data),
    delete: (id) => apiClient.delete(`/campaigns/${id}`),
    send: (id) => apiClient.post(`/campaigns/${id}/send`),
    test: (id, phone) => apiClient.post(`/campaigns/${id}/test`, { phone }),
    cancel: (id) => apiClient.put(`/campaigns/${id}/cancel`),
    duplicate: (id) => apiClient.post(`/campaigns/${id}/duplicate`),
    stats: (id) => apiClient.get(`/campaigns/${id}/stats`),
  },

  // WhatsApp
  whatsapp: {
    testConnection: () => apiClient.post('/whatsapp/test'),
    getStatus: () => apiClient.get('/whatsapp/status'),
    sendBill: (invoiceId, phone) => apiClient.post('/whatsapp/send-bill', { invoiceId, phone }),
  },

  // Feedback / Reviews
  feedback: {
    list: (params = {}) => apiClient.get('/feedback', { params }),
    stats: (params = {}) => apiClient.get('/feedback/stats', { params }),
    staffRatings: (params = {}) => apiClient.get('/feedback/staff-ratings', { params }),
    serviceRatings: (params = {}) => apiClient.get('/feedback/service-ratings', { params }),
    delete: (id) => apiClient.delete(`/feedback/${id}`),
  },

  // The walk-in queue: a visit opened on arrival rather than at payment.
  walkIns: {
    checkQuestions: (params) => apiClient.get('/walk-ins/check-questions', { params }),
    riskyCategories: () => apiClient.get('/walk-ins/risky-categories'),
    list: (params = {}) => apiClient.get('/walk-ins', { params }),
    summary: (params = {}) => apiClient.get('/walk-ins/summary', { params }),
    create: (data) => apiClient.post('/walk-ins', data),
    update: (id, data) => apiClient.patch(`/walk-ins/${id}`, data),
    start: (id, data = {}) => apiClient.patch(`/walk-ins/${id}/start`, data),
    abandon: (id, data = {}) => apiClient.patch(`/walk-ins/${id}/abandon`, data),
  },

  // The WhatsApp inbox: every conversation the chatbot has held, and where each stopped.
  whatsappConversations: {
    list: (params = {}) => apiClient.get('/whatsapp/conversations', { params }),
    detail: (phone) => apiClient.get(`/whatsapp/conversations/${phone}`),
  },

  // Post-visit reviews collected by calling the customer, one per invoice.
  invoiceReviews: {
    list: (params = {}) => apiClient.get('/invoice-reviews', { params }),
    summary: (params = {}) => apiClient.get('/invoice-reviews/summary', { params }),
    staff: (params = {}) => apiClient.get('/invoice-reviews/staff', { params }),
    staffDetail: (employeeId, params = {}) =>
      apiClient.get(`/invoice-reviews/staff/${employeeId}`, { params }),
    insights: (params = {}) => apiClient.get('/invoice-reviews/insights', { params }),
    filters: () => apiClient.get('/invoice-reviews/filters'),
    getForInvoice: (invoiceId) => apiClient.get(`/invoice-reviews/invoice/${invoiceId}`),
    save: (invoiceId, body) => apiClient.put(`/invoice-reviews/invoice/${invoiceId}`, body),
    remove: (invoiceId) => apiClient.delete(`/invoice-reviews/invoice/${invoiceId}`),
    // Can this one be answered on WhatsApp right now, and has it been answered already?
    replyWindow: (id) => apiClient.get(`/invoice-reviews/${id}/reply`),
    // One call for a whole panel: { reviewId: true|false }.
    replyWindows: (ids) => apiClient.post('/invoice-reviews/reply-windows', { ids }),
    reply: (id, body) => apiClient.post(`/invoice-reviews/${id}/reply`, body),
  },

  // Referrals
  referrals: {
    list: (params = {}) => apiClient.get('/referrals', { params }),
    create: (data) => apiClient.post('/referrals', data),
    markPaid: (id, data) => apiClient.patch(`/referrals/${id}/mark-paid`, data),
    update: (id, data) => apiClient.patch(`/referrals/${id}`, data),
    delete: (id) => apiClient.delete(`/referrals/${id}`),
    partners: {
      list: () => apiClient.get('/referrals/partners'),
      create: (data) => apiClient.post('/referrals/partners', data),
      update: (id, data) => apiClient.patch(`/referrals/partners/${id}`, data),
      delete: (id) => apiClient.delete(`/referrals/partners/${id}`),
    },
  },

  // Bridal
  // One job = an enquiry, its events, the services on each and the money against
  // them. The older consultation/quotation/booking flow that used to sit beside
  // this was removed once every one of its tables proved empty in production.
  stylistDirectory: {
    list:   (params = {}) => apiClient.get('/stylist-directory', { params }),
    create: (data)        => apiClient.post('/stylist-directory', data),
    update: (id, data)    => apiClient.put(`/stylist-directory/${id}`, data),
    remove: (id)          => apiClient.delete(`/stylist-directory/${id}`),
  },

  social: {
    overview: ()               => apiClient.get('/social/overview'),
    trend:    (params)         => apiClient.get('/social/trend', { params }),
    media:    ()               => apiClient.get('/social/media'),
    activity: (params)         => apiClient.get('/social/activity', { params }),
    posts:    (params)         => apiClient.get('/social/posts', { params }),
    refresh:  ()               => apiClient.post('/social/refresh'),
    connect:  (body)           => apiClient.post('/social/connect', body),
  },

  bridalJobs: {
    list:          (params = {}) => apiClient.get('/bridal/jobs', { params }),
    get:           (id)          => apiClient.get(`/bridal/jobs/${id}`),
    addEvent:      (id, data)    => apiClient.post(`/bridal/jobs/${id}/events`, data),
    updateEvent:   (eventId, d)  => apiClient.put(`/bridal/jobs/events/${eventId}`, d),
    removeEvent:   (eventId)     => apiClient.delete(`/bridal/jobs/events/${eventId}`),
    addService:    (eventId, d)  => apiClient.post(`/bridal/jobs/events/${eventId}/services`, d),
    updateService: (lineId, d)   => apiClient.put(`/bridal/jobs/services/${lineId}`, d),
    removeService: (lineId)      => apiClient.delete(`/bridal/jobs/services/${lineId}`),
    addPayment:    (id, data)    => apiClient.post(`/bridal/jobs/${id}/payments`, data),
    removePayment: (paymentId)   => apiClient.delete(`/bridal/jobs/payments/${paymentId}`),
    markLost:      (id, data)    => apiClient.post(`/bridal/jobs/${id}/lost`, data),
    reopen:        (id)          => apiClient.post(`/bridal/jobs/${id}/reopen`),
    remove:        (id)          => apiClient.delete(`/bridal/jobs/${id}`),
    quote:         (id, params)  => apiClient.get(`/bridal/jobs/${id}/quote`, { params }),
    sendQuote:     (id)          => apiClient.post(`/bridal/jobs/${id}/quote/whatsapp`),
    day:           (params = {})  => apiClient.get('/bridal/jobs-day', { params }),
    setProgress:   (eventId, d)   => apiClient.patch(`/bridal/jobs/events/${eventId}/progress`, d),
    eventMessage:  (eventId, p)   => apiClient.get(`/bridal/jobs/events/${eventId}/message`, { params: p }),
  },


  // Paperwork the salon keeps — agreements, invoices, safety sheets.
  tasks: {
    list: (params = {}) => apiClient.get('/tasks', { params }),
    summary: () => apiClient.get('/tasks/summary'),
    create: (data) => apiClient.post('/tasks', data),
    update: (id, data) => apiClient.patch(`/tasks/${id}`, data),
    setStatus: (id, status) => apiClient.patch(`/tasks/${id}/status`, { status }),
    note: (id, text) => apiClient.post(`/tasks/${id}/note`, { text }),
    remove: (id) => apiClient.delete(`/tasks/${id}`),
  },

  documents: {
    list: (params = {}) => apiClient.get('/documents', { params }),
    upload: (formData) => apiClient.post('/documents', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
    // A short-lived link, fetched when it is needed rather than held on the page.
    link: (id) => apiClient.get(`/documents/${id}/link`),
    addLink: (data) => apiClient.post('/documents/links', data),
    update: (id, data) => apiClient.patch(`/documents/${id}`, data),
    remove: (id) => apiClient.delete(`/documents/${id}`),
  },

  // Who is waiting for somebody to ring them back.
  customerRequests: {
    list: (params = {}) => apiClient.get('/customer-requests', { params }),
    count: () => apiClient.get('/customer-requests/count'),
    resolve: (id, data) => apiClient.patch(`/customer-requests/${id}`, data),
    // Answering them from the dashboard rather than from somebody's own WhatsApp.
    replyWindow: (id) => apiClient.get(`/customer-requests/${id}/reply`),
    reply: (id, body) => apiClient.post(`/customer-requests/${id}/reply`, body),
  },

  // What the software is set up to do — schedules, templates, and the knobs
  // that decide them. Read-only, apart from running a job by hand.
  systemConfig: {
    overview: () => apiClient.get('/system-config'),
    runJob: (id) => apiClient.post(`/system-config/jobs/${id}/run`),
  },

  // Accounts
  accounts: {
    summary: (params = {}) => apiClient.get('/accounts/summary', { params }),
    monthlyTrend: () => apiClient.get('/accounts/monthly-trend'),
    expenses: {
      list: (params = {}) => apiClient.get('/accounts/expenses', { params }),
      create: (data) => apiClient.post('/accounts/expenses', data),
      update: (id, data) => apiClient.put(`/accounts/expenses/${id}`, data),
      delete: (id) => apiClient.delete(`/accounts/expenses/${id}`),
    },
  },
};
